// Camelot notation (P3.5) — a display mapping, and nothing else.
//
// P3.5's rule: "Camelot is a display mapping — keep it out of the DSP and next to the UI that
// renders it." So this file lives under `ui/` and `audio/analysis/key.ts` has never heard of it.
// The two change for different reasons: a better chroma is a DSP change, and "show 8A rather than
// Am" is a preference. A stage that emitted "8A" would have baked a notation into the analysis
// record — and into FR-12's cache with it, where it would outlive the preference by months.
//
// ## What the wheel is
//
// Twenty-four keys arranged as twelve numbers around the circle of fifths, each with an A (minor)
// and a B (major). Neighbours differ by one accidental, so **anything adjacent mixes**: 8A → 9A,
// 8A → 7A, or 8A → 8B. That last one is the relative major, which is why the wheel is worth having
// here beyond fashion: it makes key detection's single most common error nearly harmless. C major
// misheard as A minor is 8B misheard as 8A — the same number, and a move a DJ makes on purpose.
//
// The mapping is arithmetic rather than a table, and `tests/unit/camelot.test.ts` checks all
// twenty-four against a hand-written table taken from the wheel. That is the same shape as the FFT's
// test: the reference has no algorithm in it, so the two can only agree by both being right.

import type { KeyAnalysis, KeyMode } from '../../platform/types/analysis';

/** Sharp spellings throughout. Enharmonics (F# / Gb) are the same key and a different word, and
 * picking one keeps the readout narrow and predictable; Camelot sidesteps the argument entirely. */
const NOTE_NAMES: readonly string[] = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
];

/**
 * The Camelot code for a key — `"8B"` for C major, `"8A"` for A minor.
 *
 * The arithmetic: pitch classes step by semitones and the wheel steps by fifths, so the position of
 * a key on the wheel is `7 × pitchClass mod 12` (7 is its own inverse mod 12, which is why the same
 * multiplier converts in both directions). C sits at position 0 and is numbered 8, hence the `+ 7`.
 * A minor key takes the number of its relative major, three semitones up.
 */
export function camelotCode(tonic: number, mode: KeyMode): string {
  const relativeMajor = mode === 'minor' ? (tonic + 3) % 12 : tonic;
  const number = ((((relativeMajor * 7 + 7) % 12) + 12) % 12) + 1;
  return `${String(number)}${mode === 'minor' ? 'A' : 'B'}`;
}

/** `"Am"`, `"C"` — the traditional spelling, for anyone who does not read the wheel. */
export function keyName(tonic: number, mode: KeyMode): string {
  return `${NOTE_NAMES[tonic] ?? '?'}${mode === 'minor' ? 'm' : ''}`;
}

/**
 * How sure the key has to be before the readout stops flagging it.
 *
 * Higher than the tempo's 0.6, because key detection is the less reliable of the two — published
 * implementations of this approach land around 70-80% — and because the failure is quieter. A wrong
 * BPM is obvious within four bars; a wrong key is a mix that sounds slightly sour and gets blamed on
 * the room.
 */
const KEY_SURE_ENOUGH = 0.7;

/**
 * FR-09's key readout: Camelot first, because that is what a DJ reads, with the traditional name
 * beside it.
 *
 * Every non-`done` outcome gets its own words, matching `describeLoudness` and `describeTempo`: "not
 * measured yet", "not implemented", "nothing pitched in this track" and "it threw" are four
 * different facts, and rendering all four as "—" throws the distinction away.
 */
export function describeKey(key: KeyAnalysis | undefined, semitones = 0): string {
  if (key === undefined) {
    return '…';
  }
  const { tonic, mode, confidence } = key;
  if (tonic === null || mode === null) {
    return 'no key';
  }

  // ⚠️ **The pitch fader transposes the track, and until P3.13 this readout did not know it.**
  // Vinyl mode couples rate to pitch (FR-05, and permanently so since P3.11 ruled keylock out of
  // v1), so a deck showing `8A` at +6% was out by about a semitone and at Sync's widest range by
  // five. The analysed key is a property of the file; what a DJ needs is the key *coming out of the
  // speakers*, because that is the one they are mixing against.
  //
  // **Rounded to the nearest semitone, with the shift shown beside it**, and both halves are
  // necessary. Camelot's wheel has twelve positions of one semitone each, so a fractional shift is
  // not a Camelot key at all — at +6% the track is 0.99 of a semitone above A minor, which is
  // nearest to A#m and is not exactly it. Printing the rounded code alone would be a false
  // precision; printing the shift alone would make the reader do the arithmetic. So: the nearest
  // key, and the number it is nearest *by*.
  const steps = Math.round(semitones);
  const shifted = (((tonic + steps) % 12) + 12) % 12;
  const label = `${camelotCode(shifted, mode)} ${keyName(shifted, mode)}`;
  const flagged = confidence >= KEY_SURE_ENOUGH ? label : `${label}?`;

  // A hundredth of a semitone is below anything audible and below the fader's own resolution;
  // treating it as zero keeps an untouched deck's readout clean rather than showing "+0.00 st".
  if (Math.abs(semitones) < 0.01) {
    return flagged;
  }
  const sign = semitones > 0 ? '+' : '−';
  return `${flagged} · ${sign}${Math.abs(semitones).toFixed(2)} st`;
}

/**
 * Semitones of transposition at a playback rate. `12 · log₂(rate)`.
 *
 * Exported because the readout is not the only thing that will want it — FR-13's grid tools already
 * speak in rates, and M4's key-aware FX will speak in semitones. One definition, here, beside the
 * only thing that renders it today.
 */
export function semitonesForRate(rate: number): number {
  return rate > 0 ? 12 * Math.log2(rate) : 0;
}
